// ────────────────────────────────────────────────────────────────────
// FILTER THE QUEUE — the left rail.
//
// Every control here narrows the WHOLE list, not the section nearest it. That
// is the correction this screen exists to make: the landing screen it replaces
// carried chips that filtered its first section and left the two beneath it
// untouched, so the number on the screen and the rows on the screen described
// different sets of jobs.
//
// The facet counts are deliberately the UNFILTERED ones from the wire. A facet
// list that recounts itself under its own filter collapses to a single row and
// you can no longer see what else there is to switch to; the KPI row above the
// list is the figure that must agree with what is rendered, and it does.
//
// EVERY stage is listed, numbered 1–6, including the ones holding no job. A
// stage standing empty is information — it says the pipeline is clear there —
// and the number, the label, the owning role and the count all arrive on the
// wire (`counts.by_stage`). Nothing here counts positions in an array: a
// browser numbering a list by where things landed in it is asserting an order
// the server never claimed.
//
// `A Archive · closed` carries its own figure. It is excluded from the QUEUE,
// not from the record — Archive holds both outcomes, and a closed job that
// cannot be counted is an absence indistinguishable from one that never
// happened.
//
// The throughput at the foot is the design's `THROUGHPUT · 30D`, and the
// window is printed from `counts.throughput_days` rather than typed into the
// caption — a hardcoded window is how a seven-day figure came to sit under a
// thirty-day heading in the first place.
// ────────────────────────────────────────────────────────────────────
import type { ReactNode } from "react";
import { clientKey, flowStages, allRows } from "./queueModel";
import type { QueueFilter } from "./queueModel";
import type { QueueOut } from "./types";

const MICRO = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.09em",
  textTransform: "uppercase" as const, color: "var(--ink-3)",
};

/** The stage's place in the pipeline, or `A` for Archive — the design's own
 *  numbered squares. The value is printed, never computed. */
function Marker({ children, active }: { children: ReactNode; active: boolean }) {
  return (
    <span
      style={{
        flex: "none", width: 17, height: 17, borderRadius: 4,
        display: "grid", placeItems: "center",
        fontFamily: "var(--mono)", fontSize: 9, fontWeight: 600,
        background: active ? "var(--surface-warm-2)" : "var(--line-2)",
        color: active ? "var(--accent-deep)" : "var(--ink-3)",
      }}
    >
      {children}
    </span>
  );
}

function FacetButton(
  { active, label, sub, count, testid, marker, onClick }: {
    active: boolean; label: string; sub?: string; count: number | null;
    testid: string; marker?: ReactNode; onClick: () => void;
  },
) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "baseline", gap: 8,
        textAlign: "left", cursor: "pointer",
        padding: "6px 8px", borderRadius: 3,
        border: active ? "1px solid var(--accent-deep)" : "1px solid transparent",
        background: active ? "var(--surface-warm-2)" : "transparent",
        color: active ? "var(--accent-deep)" : "var(--ink-2)",
      }}
    >
      {marker !== undefined && <Marker active={active}>{marker}</Marker>}
      <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
        {label}
        {sub && (
          <span style={{ display: "block", ...MICRO, letterSpacing: "0.06em", marginTop: 2 }}>
            {sub}
          </span>
        )}
      </span>
      {count !== null && (
        <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-3)" }}>
          {count}
        </span>
      )}
    </button>
  );
}

export function QueueSidebar(
  { data, filter, onFilter, identity }: {
    data: QueueOut;
    filter: QueueFilter | null;
    onFilter: (next: QueueFilter) => void;
    identity: { name: string; role: string } | null;
  },
) {
  const stages = flowStages(data, allRows(data));
  return (
    <aside
      data-testid="queue-sidebar"
      style={{
        borderRight: "1px solid var(--hairline)", background: "var(--surface-2)",
        padding: "16px 12px", display: "flex", flexDirection: "column", gap: 18,
      }}
    >
      <div data-testid="queue-identity">
        <div style={{ fontSize: 13, color: "var(--ink-1)", fontWeight: 600 }}>
          {identity?.name ?? "—"}
        </div>
        <div style={{ ...MICRO, marginTop: 2 }}>{identity?.role ?? "—"}</div>
      </div>

      <div>
        <div style={MICRO}>Filter the queue</div>
        <p style={{ margin: "6px 0 8px", fontSize: 12, color: "var(--ink-3)" }}>
          Click a stage or a client to narrow the queue. Click it again to clear.
        </p>
        <FacetButton
          testid="queue-facet-all"
          active={filter === null}
          label="All jobs in flight"
          count={data.counts.total}
          // Clicking a stage that is already active clears it; this row is the
          // same gesture spelled out, for when nothing is active to click twice.
          onClick={() => { if (filter !== null) onFilter(filter); }}
        />
      </div>

      <div>
        <div style={{ ...MICRO, marginBottom: 4 }}>The pipeline</div>
        {stages.map((s) => (
          <FacetButton
            key={s.stage}
            testid={`queue-facet-stage-${s.stage}`}
            active={filter?.kind === "stage" && filter.key === s.stage}
            marker={s.index}
            label={s.label}
            sub={s.ownerRole}
            count={s.count}
            onClick={() => onFilter({ kind: "stage", key: s.stage })}
          />
        ))}
        {/* Not a facet: Archive is excluded from the queue, so clicking it
            could only ever empty the list. It carries its count because the
            record is the point — a closed job that cannot be counted is an
            absence nobody can tell apart from a request that never existed. */}
        <div
          data-testid="queue-archive-note"
          style={{
            marginTop: 8, padding: "6px 8px", borderTop: "1px solid var(--line-2)",
            display: "flex", alignItems: "baseline", gap: 8,
          }}
        >
          <Marker active={false}>A</Marker>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: "var(--ink-2)" }}>Archive · closed</div>
            <div style={{ ...MICRO, letterSpacing: "0.06em", marginTop: 2 }}>
              off every queue, kept 10 yr
            </div>
          </div>
          <span
            data-testid="queue-archive-count"
            style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-3)" }}
          >
            {data.counts.archive_closed}
          </span>
        </div>
      </div>

      <div>
        <div style={{ ...MICRO, marginBottom: 4 }}>By client</div>
        {data.counts.by_client.map((c) => (
          <FacetButton
            key={clientKey(c.client_id)}
            testid={`queue-facet-client-${clientKey(c.client_id)}`}
            active={filter?.kind === "client" && filter.key === clientKey(c.client_id)}
            label={c.client ?? "—"}
            count={c.count}
            onClick={() => onFilter({ kind: "client", key: clientKey(c.client_id) })}
          />
        ))}
      </div>

      <div
        data-testid="queue-throughput"
        style={{ marginTop: "auto", paddingTop: 12, borderTop: "1px solid var(--hairline)" }}
      >
        {/* The window is the wire's, not a number typed into this caption. */}
        <div style={MICRO}>Throughput · {data.counts.throughput_days}d</div>
        <div style={{ marginTop: 4, fontSize: 13, color: "var(--ink-2)" }}>
          <span style={{ fontFamily: "var(--mono)" }}>{data.counts.throughput_closed}</span>
          {" completed"}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
          {/* Null, not zero, when nothing closed — zero would claim an instant
              close, and the wire is careful about that distinction. */}
          <span style={{ fontFamily: "var(--mono)" }}>
            {data.counts.throughput_median_days ?? "—"}
          </span>
          {" median days to close"}
        </div>
        {/* NO "breached SLA" figure here. It is not a throughput number — it
            counts the LIVE queue, not work that closed — and it was the wire's
            UNFILTERED `counts.overdue` printed under the same label as the KPI
            row's FILTERED `summary.overdue`, so with a filter on, one screen
            showed two answers to one question. The facet counts above are
            deliberately unfiltered (a facet that recounts itself collapses to a
            single row); a FIGURE is the other case, and the rule this screen
            exists to enforce is that a number must describe the rows rendered
            beside it. One figure, in the KPI row, counted off `visible`. */}
      </div>
    </aside>
  );
}
